import { AfterViewInit, ChangeDetectionStrategy, Component } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { BaseControlComponent } from './base-control.component';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class InheritedControlValueAccessorComponent<ValueAccessorType, NewType = ValueAccessorType> extends BaseControlComponent<NewType> implements AfterViewInit {

  protected abstract valueAccessor: ControlValueAccessor;

  private formControlChangedBeforeViewInit?: (value: ValueAccessorType) => void;
  private formControlTouchedBeforeViewInit?: () => void;
  private valueBeforeViewInit?: ValueAccessorType;

  public ngAfterViewInit(): void {
    // Template driven forms first call the afterViewInit and then the registerOn function
    // Reactive forms first call the registerOn then the afterViewInit function
    if (this.formControlChangedBeforeViewInit) {
      this.valueAccessor.registerOnChange(this.formControlChangedBeforeViewInit);
    }
    if (this.formControlTouchedBeforeViewInit) {
      this.valueAccessor.registerOnTouched(this.formControlTouchedBeforeViewInit);
    }
    if (this.valueBeforeViewInit !== undefined) {
      this.valueAccessor.writeValue(this.valueBeforeViewInit);
    }
    this.valueAccessor.setDisabledState?.(this.disabled);
  }

  public override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (this.valueAccessor) {
      this.valueAccessor.setDisabledState?.(isDisabled);
    }
  }

  protected convertTo(value: NewType): ValueAccessorType {
    return value as unknown as ValueAccessorType;
  }
  protected convertFrom(value: ValueAccessorType): NewType {
    return value as unknown as NewType;
  }

  public writeValue(value: NewType): void {
    const convertedValue = this.convertTo(value);
    if (!this.valueAccessor) { // This only happens in case where valueAccessor is non static
      this.valueBeforeViewInit = convertedValue;
    } else {
      this.valueAccessor.writeValue(convertedValue);
    }
  }

  public registerOnChange(onChange: (value: NewType) => void): void {
    const onChangeConverted: (value: ValueAccessorType) => void = (value: ValueAccessorType): void => {
      onChange(this.convertFrom(value));
    }

    if (this.valueAccessor) {
      this.valueAccessor.registerOnChange(onChangeConverted);
    } else {
      this.formControlChangedBeforeViewInit = onChangeConverted;
    }
  }

  public registerOnTouched(onTouched: () => void): void {
    if (this.valueAccessor) {
      this.valueAccessor.registerOnTouched(onTouched);
    } else {
      this.formControlTouchedBeforeViewInit = onTouched;
    }
  }

}
